import { useCallback } from 'react';
import { useAppSelector } from '@/store/hooks';
import { selectUser } from '@/store/user/selectors';
import { selectCurrentPage } from '@/store/pages/selectors';
import type {
  UniversalTrackingProperties,
  MixpanelPropertyValue,
  TrackFunction,
} from '@/analytics/mixpanel/types';
import mixpanel from 'mixpanel-browser';

let mixpanelInitialized = false;

/**
 * Initialize Mixpanel with the provided token and configuration.
 * This should be called once at application startup.
 *
 * Token is injected at build time from GCP Secret Manager via Cloud Build.
 * For local development, set VITE_MIXPANEL_TOKEN in .env.development.local
 */
export function setupMixpanel(): void {
  if (mixpanelInitialized) {
    console.warn('Mixpanel already initialized');
    return;
  }

  const mixpanelToken = import.meta.env.VITE_MIXPANEL_TOKEN;

  if (!mixpanelToken) {
    console.warn('Analytics tracking disabled: required configuration not found.');
    return;
  }

  try {
    mixpanel.init(mixpanelToken, {
      record_sessions_percent: Number(import.meta.env.VITE_MIXPANEL_SESSION_REPLAY_PERCENTAGE) || 0,
      record_max_ms: Number(import.meta.env.VITE_MIXPANEL_SESSION_REPLAY_LENGTH) || 3600000,
      record_mask_text_selector: import.meta.env.VITE_MIXPANEL_SESSION_REPLAY_TEXT_MASK || '*',
    });

    mixpanelInitialized = true;
  } catch (error) {
    console.error('Failed to initialize Mixpanel:', error);
  }
}

/**
 * Hook to track Mixpanel events with automatic universal properties from Redux store
 *
 * @example
 * ```tsx
 * import { useMixpanel } from '@/hooks/useMixpanel';
 * import { EVENT, SOURCE, PROPERTY } from '@/analytics/mixpanel';
 *
 * const track = useMixpanel();
 *
 * // Track with only universal properties
 * track(EVENT.TEXT_EDITED);
 *
 * // Track with additional event-specific properties
 * track(EVENT.ELEMENT_ADDED, {
 *   [PROPERTY.ELEMENT_TYPE]: 'Button',
 *   [PROPERTY.SOURCE]: SOURCE.SIDEBAR
 * });
 * ```
 */
export function useMixpanel(): TrackFunction {
  const user = useAppSelector(selectUser);
  const currentPage = useAppSelector(selectCurrentPage);

  const track = useCallback(
    (eventName: string, properties?: Record<string, MixpanelPropertyValue>) => {
      const universalProps: UniversalTrackingProperties = {
        App: 'Builder 4',
        Environment: import.meta.env.MODE,
        'Account ID': user?._meta?.id.toString() || null,
        'Asset Title': currentPage?.name || null,
        'Asset Published': !!currentPage?.publishedVersionId,
        'Plan Type': user?.subscriptionLevel || null,
      };

      const mergedProps = {
        ...universalProps,
        ...properties,
      };

      if (!mixpanelInitialized) {
        if (import.meta.env.DEV) {
          console.log('[Mixpanel]', eventName, mergedProps);
        }
        return;
      }

      mixpanel.track(eventName, mergedProps);
    },
    [user, currentPage],
  );

  return track;
}
